/**
 * Employee self-service profile — the client half.
 *
 * ── Two authentication modes, on purpose ──────────────────────────────────────────────────
 *
 * HR operations run as `userPool`, like every other admin call in this application.
 *
 * The three operations the EMPLOYEE uses run as `apiKey`, because the completion page is reached
 * from an email link by somebody who is not signed in — that is the whole point of the feature.
 * The API key is not the authorisation: it only gets the request to AppSync. What authorises the
 * call is the invitation token, which the resolver validates on every one of them. So the public
 * surface is exactly three fields, each of which refuses to do anything without a live token
 * bound to one employee.
 *
 * The token itself never identifies WHICH employee in the URL — the server resolves that from
 * the token's own record. An employee cannot reach another's profile by editing the address bar,
 * because there is nothing in the address bar to edit but an opaque secret.
 *
 * ── Why the writes take a token rather than an email ──────────────────────────────────────
 *
 * saveDraft and submit deliberately do NOT accept an employeeEmail. If they did, the email would
 * be a caller-supplied claim about whose record to write, and the token would only prove that
 * SOME invitation existed. Taking the token alone makes the record being written a property of
 * the secret, so writing to somebody else's profile is not a request that can be expressed.
 */

import { generateClient } from 'aws-amplify/api';
import {
  EmployeeSelfServiceProfile,
  ProfileCompletionStatus,
  ProfileInvitationContext,
} from '../types/employeeProfile';
import { pickEmployeeWritable } from '../config/profileFieldOwnership';
import { canonicalizeEmail } from './identity';

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const userPool = { authMode: 'userPool' as const };
/** The public completion page has no session — see the header. */
const publicMode = { authMode: 'apiKey' as const };

const getData = (result: any): any => ('data' in result ? result.data : null);

/**
 * Amplify throws `{ data, errors }` for GraphQL-level failures rather than a normal Error, so a
 * catch that reads only `.message` shows a generic fallback instead of the resolver's reason.
 * Same helper, same reasoning, as ShiftRosterService.
 */
const errorMessage = (err: any, fallback: string): string =>
  err?.errors?.[0]?.message || err?.message || fallback;

const PROFILE_FIELDS_CURRENT = `
  employeeEmail
  status
  profilePhotoKey
  personal { dateOfBirth gender bloodGroup personalEmail mobileNumber fathersName mothersName maritalStatus marriageDate spouseName nationality }
  address {
    presentSameAsPermanent
    permanent { line1 line2 city district state country pincode }
    present { line1 line2 city district state country pincode }
  }
  emergencyContact { name mobileNumber relationship }
  qualifications { id qualification institute fromYear toYear grade documentKeys }
  previousEmployment { id companyName designation fromDate toDate lastDrawnSalary reasonForLeaving companyAddress documentKeys }
  identifications { aadhaarNumber panNumber passportNumber passportExpiry drivingLicence aadhaarDocumentKeys panDocumentKeys documentKeys }
  family { id name relationship dateOfBirth occupation isDependent }
  nominations { id nomineeName relationship dateOfBirth sharePercentage nominationFor }
  accounts { bankName branch accountNumber ifscCode nameAsPerBank pfNumber uanNumber esiNumber documentKeys }
  documentKeys
  pendingChanges { id employeeEmail section path label oldValue newValue submittedBy submittedByRole submittedAt status decidedBy decidedAt decisionReason documentKey }
  history { id employeeEmail section path label oldValue newValue submittedBy submittedByRole submittedAt status decidedBy decidedAt decisionReason documentKey }
  invitationSentAt
  invitationSentBy
  invitationExpiresAt
  submittedAt
  updatedAt
`;

/*
 * The same selection without the Aadhaar/PAN document slots.
 *
 * GraphQL rejects the whole document for one unknown field, and the AppSync schema is applied by
 * a manual Terraform run — so until it carries aadhaarDocumentKeys/panDocumentKeys, asking for
 * them would take every profile page down. The reads retry once with this instead. Drop it, and
 * withProfileFields, once the schema is deployed everywhere.
 */
const PROFILE_FIELDS_LEGACY = PROFILE_FIELDS_CURRENT.replace(' aadhaarDocumentKeys panDocumentKeys', '');
let PROFILE_FIELDS = PROFILE_FIELDS_CURRENT;

// ─── HR operations (userPool) ─────────────────────────────────────────────────────────────

const listProfilesQuery = () => /* GraphQL */ `
  query ListEmployeeProfiles($limit: Int, $nextToken: String) {
    listEmployeeSelfServiceProfiles(limit: $limit, nextToken: $nextToken) {
      items { ${PROFILE_FIELDS} }
      nextToken
    }
  }
`;

const getProfileQuery = () => /* GraphQL */ `
  query GetEmployeeProfile($employeeEmail: String!) {
    getEmployeeSelfServiceProfile(employeeEmail: $employeeEmail) { ${PROFILE_FIELDS} }
  }
`;

const decideChangesMutation = /* GraphQL */ `
  mutation DecideProfileChanges($input: AWSJSON!) {
    decideEmployeeProfileChanges(input: $input) { employeeEmail status updatedAt }
  }
`;

const hrUpdateMutation = /* GraphQL */ `
  mutation HrUpdateEmployeeProfile($employeeEmail: String!, $input: AWSJSON!) {
    hrUpdateEmployeeProfile(employeeEmail: $employeeEmail, input: $input) {
      employeeEmail
      status
      updatedAt
    }
  }
`;

/* No employeeEmail: the server writes the caller's own record. See proposeChanges. */
const proposeChangesMutation = /* GraphQL */ `
  mutation ProposeEmployeeProfileChanges($input: AWSJSON!) {
    proposeEmployeeProfileChanges(input: $input) { employeeEmail status updatedAt }
  }
`;

const sendInvitationMutation = /* GraphQL */ `
  mutation SendProfileInvitation($employeeEmail: String!) {
    sendEmployeeProfileInvitation(employeeEmail: $employeeEmail) {
      employeeEmail
      status
      invitationSentAt
      invitationExpiresAt
    }
  }
`;

// ─── Employee operations (apiKey + token) ─────────────────────────────────────────────────

const invitationQuery = () => /* GraphQL */ `
  query GetProfileInvitation($token: String!) {
    getEmployeeProfileInvitation(token: $token) {
      employeeEmail
      employeeName
      employeeId
      designation
      department
      dateOfJoining
      expiresAt
      profile { ${PROFILE_FIELDS} }
    }
  }
`;

const saveDraftMutation = /* GraphQL */ `
  mutation SaveEmployeeProfileDraft($token: String!, $input: AWSJSON!) {
    saveEmployeeProfileDraft(token: $token, input: $input) { employeeEmail status updatedAt }
  }
`;

const submitMutation = /* GraphQL */ `
  mutation SubmitEmployeeProfile($token: String!, $input: AWSJSON!) {
    submitEmployeeProfile(token: $token, input: $input) { employeeEmail status submittedAt }
  }
`;

/**
 * Raised when the backend for this feature is not deployed in the current environment.
 *
 * Distinguished from a genuine failure because the two need different answers: "ask your
 * administrator to deploy the profile service" versus "try again". Without the distinction the
 * whole feature looks broken in any environment where the schema has not been applied yet, and
 * this repo's AppSync schema is applied by a manual Terraform run.
 */
export const PROFILE_BACKEND_UNAVAILABLE =
  'The employee profile service is not available in this environment yet. ' +
  'It needs the AppSync schema and the profile table to be deployed.';

/** Whether a GraphQL error means "this field does not exist on the deployed schema". */
const isUnknownField = (err: any): boolean => {
  const message = String(err?.errors?.[0]?.message || err?.message || '');
  return /Unknown field|Validation error|Cannot query field|FieldUndefined/i.test(message);
};

/**
 * Run a profile read, falling back once to PROFILE_FIELDS_LEGACY on an unknown-field error.
 *
 * The fallback sticks for the session, so only the first read pays the extra round trip. An
 * unknown-field error that survives the retry is the whole feature missing, and is rethrown.
 */
const withProfileFields = async <T,>(run: () => Promise<T>): Promise<T> => {
  try {
    return await run();
  } catch (err: any) {
    if (!isUnknownField(err) || PROFILE_FIELDS === PROFILE_FIELDS_LEGACY) throw err;
    PROFILE_FIELDS = PROFILE_FIELDS_LEGACY;
    return run();
  }
};

export const EmployeeProfileService = {
  /** Every self-service profile, for the HR list. */
  async list(): Promise<EmployeeSelfServiceProfile[]> {
    try {
      const result = await withProfileFields(() => getClient().graphql({
        query: listProfilesQuery(),
        variables: { limit: 500 },
        ...userPool,
      }));
      return getData(result)?.listEmployeeSelfServiceProfiles?.items?.filter(Boolean) || [];
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'Could not load employee profiles.'));
    }
  },

  /** One profile, for the HR detail page. Undefined when the employee has never been invited. */
  async get(employeeEmail: string): Promise<EmployeeSelfServiceProfile | undefined> {
    try {
      const result = await withProfileFields(() => getClient().graphql({
        query: getProfileQuery(),
        variables: { employeeEmail: canonicalizeEmail(employeeEmail) },
        ...userPool,
      }));
      return getData(result)?.getEmployeeSelfServiceProfile || undefined;
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'Could not load the employee profile.'));
    }
  },

  /**
   * Issue (or re-issue) an invitation and email it to the employee.
   *
   * The token is generated SERVER-side and never returned here — only the fact that one was
   * sent, and when it expires. A token that came back to the browser would be one an HR admin
   * could copy, and the link is meant to prove the recipient controls that mailbox.
   *
   * Re-issuing is how "Resend" works, and it invalidates the previous link rather than adding a
   * second live one, so a forwarded old email stops working the moment a new one is sent.
   */
  async sendInvitation(employeeEmail: string): Promise<{ expiresAt?: string }> {
    try {
      const result = await getClient().graphql({
        query: sendInvitationMutation,
        variables: { employeeEmail: canonicalizeEmail(employeeEmail) },
        ...userPool,
      });
      const sent = getData(result)?.sendEmployeeProfileInvitation;
      return { expiresAt: sent?.invitationExpiresAt };
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'Could not send the invitation email.'));
    }
  },

  /**
   * Approve or reject a set of pending changes.
   *
   * Takes IDS rather than the changed values. The values are already stored on the pending
   * record, and re-sending them would let a decision carry a different value from the one HR
   * was looking at when they clicked Approve — which is the one thing an approval must not be
   * able to do.
   *
   * Batched because HR rules on a screenful at a time, and one round trip per field would make
   * approving a completed profile forty requests.
   */
  async decideChanges(
    employeeEmail: string,
    decisions: { changeId: string; decision: 'APPROVED' | 'REJECTED' | 'CORRECTION_REQUESTED'; reason?: string }[]
  ): Promise<void> {
    try {
      await getClient().graphql({
        query: decideChangesMutation,
        variables: {
          input: JSON.stringify({
            employeeEmail: canonicalizeEmail(employeeEmail),
            decisions,
          }),
        },
        ...userPool,
      });
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'The decision could not be recorded.'));
    }
  },

  /**
   * HR editing self-service data directly.
   *
   * Applied as approved immediately — HR is the approving authority, so requiring them to
   * approve their own edit would be a signature on their own signature. An audit entry is still
   * written, attributed to them, because "HR changed this" is exactly what somebody reading the
   * history later needs to know.
   */
  async hrUpdate(
    employeeEmail: string,
    profile: Partial<EmployeeSelfServiceProfile>
  ): Promise<void> {
    try {
      await getClient().graphql({
        query: hrUpdateMutation,
        variables: {
          employeeEmail: canonicalizeEmail(employeeEmail),
          input: JSON.stringify(pickEmployeeWritable(profile)),
        },
        ...userPool,
      });
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'The update could not be saved.'));
    }
  },

  /**
   * An employee proposing changes to their own record, for HR to approve.
   *
   * Takes no email. The server writes the CALLER's record, resolved from the pool, so this
   * cannot be aimed at anybody else however it is called.
   *
   * Nothing it sends is applied. The server diffs the patch against the approved data and files
   * each changed field as PENDING — the employee's own page keeps showing the approved values
   * until HR decides, which is the whole point of the queue.
   */
  async proposeChanges(profile: Partial<EmployeeSelfServiceProfile>): Promise<void> {
    try {
      await getClient().graphql({
        query: proposeChangesMutation,
        variables: { input: JSON.stringify(pickEmployeeWritable(profile)) },
        ...userPool,
      });
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'Your changes could not be sent for approval.'));
    }
  },

  // ── Employee-facing, token-authorised ──────────────────────────────────────────────────

  /** Resolve a token into the page's context. Throws when it is unknown, used or expired. */
  async openInvitation(token: string): Promise<ProfileInvitationContext> {
    try {
      const result = await withProfileFields(() => getClient().graphql({
        query: invitationQuery(),
        variables: { token },
        ...publicMode,
      }));
      const context = getData(result)?.getEmployeeProfileInvitation;
      if (!context) {
        throw new Error(
          'This link is no longer valid. It may have expired, or a newer invitation may have ' +
            'been sent. Ask HR to send you a new one.'
        );
      }
      return context;
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'This profile link could not be opened.'));
    }
  },

  /**
   * Save progress without submitting.
   *
   * pickEmployeeWritable is applied HERE as well as on the server. Not belt-and-braces: it means
   * a bug in the form cannot send a server-owned field and get an opaque rejection halfway
   * through onboarding — the field is dropped in the browser, where the mistake is visible.
   */
  async saveDraft(
    token: string,
    profile: Partial<EmployeeSelfServiceProfile>
  ): Promise<ProfileCompletionStatus> {
    try {
      const result = await getClient().graphql({
        query: saveDraftMutation,
        variables: { token, input: JSON.stringify(pickEmployeeWritable(profile)) },
        ...publicMode,
      });
      return getData(result)?.saveEmployeeProfileDraft?.status || ProfileCompletionStatus.IN_PROGRESS;
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'Your progress could not be saved.'));
    }
  },

  /** Final submission. The server re-runs validation and marks the profile COMPLETED. */
  async submit(
    token: string,
    profile: Partial<EmployeeSelfServiceProfile>
  ): Promise<{ submittedAt?: string }> {
    try {
      const result = await getClient().graphql({
        query: submitMutation,
        variables: { token, input: JSON.stringify(pickEmployeeWritable(profile)) },
        ...publicMode,
      });
      return { submittedAt: getData(result)?.submitEmployeeProfile?.submittedAt };
    } catch (err: any) {
      if (isUnknownField(err)) throw new Error(PROFILE_BACKEND_UNAVAILABLE);
      throw new Error(errorMessage(err, 'Your profile could not be submitted.'));
    }
  },
};

/**
 * The link that goes in the invitation email.
 *
 * Built from the deployed app origin rather than window.location, because the sender may be an
 * admin on localhost and the recipient certainly is not. The token is a path segment rather than
 * a query parameter so it stays out of Referer headers on any third-party asset the page loads.
 */
export const profileCompletionUrl = (token: string): string => {
  const base = (process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin).replace(/\/$/, '');
  return `${base}/complete-profile/${encodeURIComponent(token)}`;
};
